export interface Stat {
  value: string
  label: string
}

export interface Metric {
  value: string
  label: string
}

export interface Volume {
  number: string
  title: string
  pages: string
  body: string
  tags: string[]
}

export interface Bonus {
  number: string
  title: string
  value: string
  body: string
  cover: string
}

export interface Quote {
  initials: string
  name: string
  role: string
  quote: string
}

export interface Faq {
  question: string
  answer: string
}

export interface ComparisonRow {
  without: string
  with: string
}

export const images = {
  heroMockup:
    'https://cdn.magicpatterns.com/patterns/generated-images/86b5b74f-ff3e-493b-b460-2ec6bf3e497e.jpg',
  sheetsFlatlay:
    'https://cdn.magicpatterns.com/patterns/generated-images/05603c8a-502a-4a27-ab59-0a7108953045.jpg',
  tracksVisual:
    'https://cdn.magicpatterns.com/patterns/generated-images/d62c7a84-69e3-4354-b883-5f80f004b038.jpg',
}

export const heroBadges = [
  '6 complete volumes',
  '5 exclusive bonuses',
  '3,054+ ready-to-print pages',
  '540+ exercises & worksheets',
  'All ages & skill levels',
]

export const heroStats: Stat[] = [
  { value: '3,054+', label: 'Ready-to-print pages' },
  { value: '6 + 5', label: 'Volumes and bonuses included' },
  { value: '50+', label: 'Structured lesson plans' },
  { value: 'Kids to adults', label: 'Beginner through advanced' },
]

export const heroQuotes: Quote[] = [
  {
    initials: 'SJ',
    name: 'Sarah J.',
    role: 'Piano Teacher',
    quote:
      'Before TeachersKit, I was spending 5 hours a week just looking for things to teach. Now, I print and go.',
  },
  {
    initials: 'DM',
    name: 'Daniel M.',
    role: 'Guitar Teacher',
    quote:
      'My beginners are playing real music by week three. The strumming sheets alone were worth the price.',
  },
  {
    initials: 'LC',
    name: 'Laura C.',
    role: 'Music Academy Director',
    quote:
      'Every teacher in my academy now works from the same plan. Parents can finally see the progress.',
  },
]

export const pains: string[] = [
  'You spend hours scouring Pinterest and YouTube for material that looks professional, only to find junk',
  'You repeat the same worksheets because you’ve run out of fresh material',
  'You don’t have a logical progression mapped out for the whole year',
  'You’re losing students after month two because they get bored with old, dusty method books',
  'You feel unprepared and stressed before every single lesson',
  'You’re a great musician, but you’re exhausted from improvising every class',
]

export const solutionMetrics: Metric[] = [
  { value: '3,054+', label: 'Practical pages' },
  { value: '540+', label: 'Ready-to-teach exercises' },
  { value: '50+', label: 'Complete lesson plans' },
  { value: '6', label: 'Complete volumes' },
]

export const coverage: { number: string; label: string }[] = [
  { number: '01', label: 'Beginner Foundations' },
  { number: '02', label: 'Intermediate Repertoire' },
  { number: '03', label: 'Advanced Technique' },
  { number: '04', label: 'Piano & Guitar Tracks' },
  { number: '05', label: 'Theory, Reading & Games' },
  { number: '06', label: 'Assessment & Progress Tracking' },
]

export const volumes: Volume[] = [
  {
    number: '01',
    title: 'Piano Foundations',
    pages: '509 pages',
    body: 'From “The Ebony Spider” onward — the exercises that get absolute beginners playing recognizable music in record time.',
    tags: ['First Notes', 'The Ebony Spider', 'Five-Finger Patterns', 'Sight Reading'],
  },
  {
    number: '02',
    title: 'Piano Mastery & Hand Independence',
    pages: '509 pages',
    body: 'The intermediate half of the piano series: 270+ exercises building coordination, dynamics, and advanced hand independence.',
    tags: ['Hand Independence', 'Scales & Arpeggios', 'Dynamics', 'Repertoire'],
  },
  {
    number: '03',
    title: 'Guitar Fretboard Foundations',
    pages: '509 pages',
    body: 'Master the fretboard from the first lesson: open chords, power chords, and scales that actually make sense to a beginner.',
    tags: ['Open Chords', 'Power Chords', 'Fretboard Maps', 'Beginner Scales'],
  },
  {
    number: '04',
    title: 'Guitar Rhythm & Performance',
    pages: '509 pages',
    body: '270+ sheets covering the “Heartbeat Strumming Pattern,” riffs, and performance-ready songs for solo and group settings.',
    tags: ['Heartbeat Strumming', 'Riffs', 'Song Sheets', 'Performance Prep'],
  },
  {
    number: '05',
    title: 'Applied Theory & Reading',
    pages: '509 pages',
    body: 'No more boring lectures. Visual American chord symbols, rhythmic dictation games, and “Note Soup” worksheets that make reading fun.',
    tags: ['Chord Symbols', 'Rhythmic Dictation', 'Note Soup', 'Theory Games'],
  },
  {
    number: '06',
    title: 'The Teacher’s Operations Manual',
    pages: '509 pages',
    body: 'Your year, planned. 50+ lesson plans, assessment rubrics, and the “30-Day Rockstar Plan” so every student feels like a winner.',
    tags: ['50+ Lesson Plans', 'Rubrics', '30-Day Rockstar Plan', 'Parent Reports'],
  },
]

export const bonuses: Bonus[] = [
  {
    number: '01',
    title: 'Professional Backing Tracks Pack',
    value: '$97',
    body: '50 professional tracks in Pop, Rock, and Jazz so your students never have to practice alone.',
    cover: 'https://cdn.magicpatterns.com/uploads/jMQ7bJEFZ2pQLxHGTrpTVT/musica_1.webp',
  },
  {
    number: '02',
    title: 'The Student Motivation Playbook',
    value: '$97',
    body: '47 proven tactics to keep students excited about practicing at home.',
    cover: 'https://cdn.magicpatterns.com/uploads/2GXHyKzKjc6pXcnNhwNwsU/musica_.webp',
  },
  {
    number: '03',
    title: 'The Music Business Operations Kit',
    value: '$127',
    body: 'Editable contracts, cancellation policies, and financial trackers to run your studio like a pro.',
    cover: 'https://cdn.magicpatterns.com/uploads/j9pqRS6uyfy5jTraVxmZdU/musica_4.webp',
  },
  {
    number: '04',
    title: 'Parent Communication Kit',
    value: '$47',
    body: 'Progress reports, milestone certificates, and message templates that keep parents informed and happy.',
    cover: 'https://cdn.magicpatterns.com/uploads/1o5nf3cZ5wY67aQMJmWveU/muscia_3.webp',
  },
  {
    number: '05',
    title: 'How to Charge More for Your Lessons',
    value: '$150',
    body: 'The positioning and pricing playbook to raise your rates without losing a single student.',
    cover: 'https://cdn.magicpatterns.com/uploads/gi6gHGL4ECjcmDPtJg4T42/musica_2.webp',
  },
]

export const packageIncludes: string[] = [
  '6 complete teaching volumes',
  '5 exclusive bonus guides — free',
  '3,054+ ready-to-print pages',
  '50+ structured lesson plans',
  'Piano and guitar, beginner through advanced',
  'Unlimited lifetime printing license',
]

export const testimonials: Quote[] = [
  {
    initials: 'SJ',
    name: 'Sarah J.',
    role: 'Piano Teacher',
    quote:
      'Before TeachersKit, I was spending 5 hours a week just looking for things to teach. Now, I print and go. My students are making faster progress, and I haven’t had a single dropout in 3 months.',
  },
  {
    initials: 'DM',
    name: 'Daniel M.',
    role: 'Guitar Teacher',
    quote:
      'The strumming and power chord sheets got my beginners playing real songs by week three. Nothing else I bought did that.',
  },
  {
    initials: 'LC',
    name: 'Laura C.',
    role: 'Music Academy Director',
    quote:
      'Every teacher in my academy now works from the same material. The printing license alone made this a no-brainer.',
  },
  {
    initials: 'RT',
    name: 'Rachel T.',
    role: 'Private Studio Owner',
    quote:
      'The assessment rubrics changed my parent meetings. I can show concrete progress instead of just saying “she’s doing well.”',
  },
  {
    initials: 'MO',
    name: 'Marco O.',
    role: 'Piano & Guitar Teacher',
    quote:
      'I teach both instruments, so having piano and guitar in one system means my Sunday planning is genuinely done in minutes.',
  },
]

export const guaranteeChips = ['Simple refund', 'No forms to fill', 'Keep all bonuses']

export const faqs: Faq[] = [
  {
    question: 'Is this a physical book?',
    answer:
      'This is a digital PDF bundle. You get instant access to download and print everything immediately.',
  },
  {
    question: 'Does it work for all ages?',
    answer:
      'Yes. The pedagogy is designed for kids, teens, and adults. The “Success-First” method works for everyone.',
  },
  {
    question: 'Can I use this for my music academy?',
    answer:
      'Yes! The Unlimited Printing License allows you to use it for all your students in your studio or academy.',
  },
  {
    question: 'How do I access the material?',
    answer:
      'You get a download link the moment you check out, plus lifetime access to every future update at no extra cost.',
  },
  {
    question: 'What if I’m not completely satisfied?',
    answer:
      'You have 30 days. If your students aren’t more engaged, or you don’t save at least 5 hours of planning a week, email us and we’ll refund every cent — no questions asked.',
  },
]

export const comparison: ComparisonRow[] = [
  {
    without: '5+ hours a week hunting for worksheets',
    with: 'Print and go · Everything already prepared',
  },
  {
    without: 'Improvised lessons · No clear progression',
    with: '3,054+ pages · Logical week-by-week progression',
  },
  {
    without: 'Students bored by dusty method books',
    with: 'Backing tracks, games and songs they ask for',
  },
  {
    without: 'Parents asking “where are the results?”',
    with: 'Rubrics, diplomas and progress reports ready',
  },
  {
    without: 'Sunday nights lost to formatting worksheets',
    with: 'Your whole year mapped · Sundays back',
  },
]
